import React, { useId, useState } from 'react';
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Skeleton, Stack, Typography } from '@mui/material';
import Description from '@mui/icons-material/Description';
import Download from '@mui/icons-material/Download';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import TableChart from '@mui/icons-material/TableChart';
import Article from '@mui/icons-material/Article';
import { documentType, resolveFileUrl } from './documentUtils';

function Placeholder({ text, height, children }) {
  return (
    <Stack alignItems="center" justifyContent="center" spacing={.5}
      sx={{ minHeight: Math.min(height, 72), bgcolor: '#F4F7FB', borderRadius: 2, p: 1, textAlign: 'center' }}>
      <ImageIcon color="disabled" />
      <Typography variant="caption" color="text.secondary">{text}</Typography>
      {children}
    </Stack>
  );
}

// A changed URL remounts the content so old load/error/dialog state cannot leak.
export default function DocumentViewer({ path, ...props }) {
  const url = resolveFileUrl(path);
  const missing = path == null || (typeof path === 'string' && (!path.trim() || /^(null|undefined|false)$/i.test(path.trim())));
  return <DocumentContent key={url || 'missing'} url={url} missing={missing} {...props} />;
}

const DEFAULT_ACTIONS = { download: 'Download', viewPdf: 'View PDF', preview: 'Preview', close: 'Close' };

function DocumentContent({ url, missing, label = 'Document', height = 150, gallery = false, actionLabels }) {
  const actions = { ...DEFAULT_ACTIONS, ...actionLabels };
  const [loaded, setLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [pdfError, setPdfError] = useState(false);
  const [preview, setPreview] = useState(false);
  const titleId = useId();
  if (!url) return <Placeholder height={height} text={missing ? 'No Document Uploaded' : 'Invalid attachment path'} />;

  const type = documentType(url);
  const Icon = type === 'pdf' ? PictureAsPdf : type === 'word' ? Article : type === 'spreadsheet' ? TableChart : Description;
  let filename;
  try { filename = decodeURIComponent(new URL(url).pathname.split('/').pop()) || label; } catch { filename = label; }
  // Cross-origin download disposition is controlled by the server/browser. A
  // normal link keeps working without CORS-readable bytes or invented auth.
  const downloadAction = (
    <Button size="small" component="a" href={url} download={filename} target="_blank" rel="noopener noreferrer" startIcon={<Download />}>
      {actions.download}
    </Button>
  );
  const canPreview = type === 'image' || type === 'pdf';

  return (
    <Box sx={{ minWidth: 0 }}>
      {type === 'image' ? imageError ? (
        <Placeholder height={height} text="Image unavailable" />
      ) : (
        <Box component="button" type="button" onClick={() => setPreview(true)} aria-label={`Enlarge ${label}`}
          sx={{ position: 'relative', p: 0, border: 0, width: '100%', height, display: 'block', bgcolor: '#F4F7FB', borderRadius: 2, overflow: 'hidden', cursor: 'zoom-in' }}>
          {!loaded && <Skeleton variant="rounded" aria-label={`Loading ${label}`} sx={{ position: 'absolute', inset: 0, height: '100%' }} />}
          <Box component="img" src={url} alt={label} onLoad={() => setLoaded(true)} onError={() => setImageError(true)}
            sx={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block', opacity: loaded ? 1 : 0 }} />
        </Box>
      ) : (
        <Stack alignItems="center" justifyContent="center" spacing={1}
          sx={{ minHeight: gallery ? height : Math.min(height, 130), bgcolor: '#F4F7FB', borderRadius: 2, p: 1.5 }}>
          <Icon sx={{ fontSize: 38, color: type === 'pdf' ? '#DC2626' : type === 'spreadsheet' ? '#15803D' : '#2563EB' }} />
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere', textAlign: 'center' }}>{filename}</Typography>
          <Typography variant="caption" color="text.secondary">
            {type === 'word' ? 'Word document' : type === 'spreadsheet' ? 'Spreadsheet' : type === 'pdf' ? 'PDF document' : 'File attachment'}
          </Typography>
          {gallery && (canPreview ? <Button size="small" onClick={() => setPreview(true)}>{actions.viewPdf}</Button> : downloadAction)}
        </Stack>
      )}
      {!gallery && !imageError && (
        <Stack direction="row" useFlexGap flexWrap="wrap" gap={1} mt={1}>
          {canPreview && <Button size="small" onClick={() => setPreview(true)}>{type === 'pdf' ? actions.viewPdf : actions.preview}</Button>}
          {downloadAction}
        </Stack>
      )}
      <Dialog open={preview} onClose={() => setPreview(false)} fullScreen aria-labelledby={titleId}>
        <DialogTitle id={titleId}>{label}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', bgcolor: '#F4F7FB', p: 1 }}>
          {type === 'image' && !imageError && (
            <Box component="img" src={url} alt={`${label} full-size preview`} onError={() => setImageError(true)}
              sx={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} />
          )}
          {imageError && <Placeholder height={height} text="Image unavailable" />}
          {type === 'pdf' && (
            <>
              {!pdfError && (
                <Box component="object" data={url} type="application/pdf" aria-label={`${label} PDF preview`}
                  sx={{ width: '100%', flex: 1, minHeight: '65vh' }}>
                  <Typography>PDF preview unavailable in this browser. Use Download instead.</Typography>
                </Box>
              )}
              {/* Browsers do not reliably expose cross-origin frame failures. */}
              {!pdfError && <Button size="small" onClick={() => setPdfError(true)}>Preview not displaying?</Button>}
              <Typography variant="body2" color="text.secondary" sx={{ p: 2, textAlign: 'center' }}>
                {pdfError ? 'PDF preview unavailable. ' : 'If the PDF is blank or blocked, '}
                use Download. Server permissions and browser settings still apply.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ flexWrap: 'wrap' }}>{downloadAction}<Button onClick={() => setPreview(false)}>{actions.close}</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
